import { printersInProgressSet } from './printersOverview';
import { filterActivePrinters } from './printers';
import {
    anchorActivityAt,
    openRestrictionForPrinter,
} from './printerProductivity';

/** Impressora parada: sem job em andamento e último job há mais de 10 minutos. */
export const IDLE_THRESHOLD_MS = 10 * 60 * 1000;

/** Instantâneo de atividade do job (fim > início > criação). */
export function jobActivityAt(job) {
    if (!job) {
        return null;
    }
    const raw = job.finished_at || job.started_at || job.created_at || job.updated_at;
    if (!raw) {
        return null;
    }
    const ms = Date.parse(raw);
    return Number.isFinite(ms) ? ms : null;
}

/** Última atividade por impressora a partir de uma lista de jobs (mais recente vence). */
export function lastActivityByPrinter(jobs) {
    const map = new Map();
    (jobs || []).forEach((job) => {
        const printerId = job?.printer_uuid;
        if (!printerId) {
            return;
        }
        const at = jobActivityAt(job);
        if (at == null) {
            return;
        }
        const prev = map.get(printerId);
        if (prev == null || at > prev) {
            map.set(printerId, at);
        }
    });
    return map;
}

/** Duração legível de ociosidade (ex.: 12min, 2h 15min, 3d 4h). */
export function formatIdleDuration(ms) {
    const totalMinutes = Math.max(0, Math.floor(Number(ms) / 60000) || 0);
    if (totalMinutes < 60) {
        return `${totalMinutes}min`;
    }
    const hours = Math.floor(totalMinutes / 60);
    const remMin = totalMinutes % 60;
    if (hours < 48) {
        return remMin ? `${hours}h ${remMin}min` : `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return remHours ? `${days}d ${remHours}h` : `${days}d`;
}

/** Label atual a partir do instante do último job (ou "sem jobs"). */
export function resolveIdleLabel(lastActivityAt, now = Date.now()) {
    if (lastActivityAt == null || !Number.isFinite(Number(lastActivityAt))) {
        return 'sem jobs';
    }
    return formatIdleDuration(Math.max(0, Number(now) - Number(lastActivityAt)));
}

/** Texto completo da flag (PARADA há … / OCIOSA · sem jobs). */
export function formatIdleFlagText(idleLabel) {
    if (!idleLabel) {
        return null;
    }
    return idleLabel === 'sem jobs'
        ? 'OCIOSA · sem jobs'
        : `PARADA há ${idleLabel}`;
}

/**
 * Status runtime a partir de impressão + último job.
 * lastActivityAt === null → sem jobs (ociosa).
 * lastActivityAt número → ociosa só após o threshold.
 */
export function runtimeStatusFromActivity({
    isPrinting = false,
    lastActivityAt,
    now = Date.now(),
    thresholdMs = IDLE_THRESHOLD_MS,
} = {}) {
    if (isPrinting) {
        return {
            is_printing: true,
            is_idle: false,
            idle_label: null,
            idle_ms: null,
            last_activity_at: null,
        };
    }

    if (lastActivityAt == null) {
        return {
            is_printing: false,
            is_idle: true,
            idle_label: 'sem jobs',
            idle_ms: null,
            last_activity_at: null,
        };
    }

    const at = Number(lastActivityAt);
    if (!Number.isFinite(at)) {
        return {
            is_printing: false,
            is_idle: true,
            idle_label: 'sem jobs',
            idle_ms: null,
            last_activity_at: null,
        };
    }

    const idleMs = Math.max(0, Number(now) - at);
    const isIdle = idleMs >= thresholdMs;
    return {
        is_printing: false,
        is_idle: isIdle,
        idle_ms: idleMs,
        idle_label: isIdle ? formatIdleDuration(idleMs) : null,
        last_activity_at: at,
    };
}

/** Recalcula is_idle no front a partir de last_activity_at (tick). */
export function refreshPrinterIdleFlags(items, now = Date.now()) {
    return (items || []).map((item) => {
        if (item?.is_printing) {
            return {
                ...item,
                ...runtimeStatusFromActivity({ isPrinting: true, now }),
            };
        }
        if (!Object.prototype.hasOwnProperty.call(item || {}, 'last_activity_at')) {
            return item;
        }
        return {
            ...item,
            ...runtimeStatusFromActivity({
                isPrinting: false,
                lastActivityAt: item.last_activity_at,
                now,
            }),
        };
    });
}

/**
 * Impressoras ativas paradas (sem impressão agora e último job > threshold).
 * Ordenadas pela ociosidade mais longa primeiro.
 */
export function buildIdlePrinterRows({
    printers,
    inProgressJobs,
    recentJobs,
    restrictions,
    now = Date.now(),
    thresholdMs = IDLE_THRESHOLD_MS,
} = {}) {
    const activityMap = buildPrinterActivityMap({
        printers,
        inProgressJobs,
        recentJobs,
        restrictions,
    });
    const rows = [];

    activityMap.forEach((entry, id) => {
        const status = runtimeStatusFromActivity({
            isPrinting: false,
            lastActivityAt: entry.lastActivityAt,
            now,
            thresholdMs,
        });
        if (!status.is_idle) {
            return;
        }
        rows.push({
            id,
            name: entry.name || id,
            lastActivityAt: entry.lastActivityAt,
            idleMs: status.idle_ms,
            idleLabel: status.idle_label,
        });
    });

    rows.sort((a, b) => {
        if (a.idleMs == null && b.idleMs == null) {
            return String(a.name).localeCompare(String(b.name), 'pt-BR');
        }
        if (a.idleMs == null) {
            return 1;
        }
        if (b.idleMs == null) {
            return -1;
        }
        return b.idleMs - a.idleMs;
    });

    return rows;
}

/** Mapa de última atividade das impressoras que NÃO estão imprimindo. */
export function buildPrinterActivityMap({
    printers,
    inProgressJobs,
    recentJobs,
    restrictions,
} = {}) {
    const active = filterActivePrinters(printers);
    const busy = printersInProgressSet(inProgressJobs);
    const lastByPrinter = lastActivityByPrinter([
        ...(recentJobs || []),
        ...(inProgressJobs || []),
    ]);
    const map = new Map();

    active.forEach((printer) => {
        const id = printer.id || printer.uuid;
        if (!id || busy.has(id) || openRestrictionForPrinter(restrictions, id)) {
            return;
        }
        const jobAt = lastByPrinter.has(id) ? lastByPrinter.get(id) : null;
        map.set(id, {
            id,
            name: printer.name || id,
            lastActivityAt: anchorActivityAt(jobAt, restrictions, id),
        });
    });

    return map;
}

export function idleRowsToMap(rows) {
    return new Map((rows || []).map((row) => [row.id, row]));
}

export function activityMapToIdleMap(activityMap, now = Date.now()) {
    const rows = [];
    (activityMap || new Map()).forEach((entry, id) => {
        const status = runtimeStatusFromActivity({
            isPrinting: false,
            lastActivityAt: entry?.lastActivityAt,
            now,
        });
        if (!status.is_idle) {
            return;
        }
        rows.push({
            id,
            name: entry?.name || id,
            lastActivityAt: entry?.lastActivityAt ?? null,
            idleMs: status.idle_ms,
            idleLabel: status.idle_label,
        });
    });
    return idleRowsToMap(rows);
}

/** Anexa flags de runtime (impressão / ociosidade) usando mapa de atividade. */
export function applyPrinterRuntimeFlags(items, {
    printingSet,
    activityMap,
    now = Date.now(),
} = {}) {
    return (items || []).map((item) => {
        const id = item?.printer_uuid || item?.id;
        const isPrinting = printingSet instanceof Set
            ? printingSet.has(id)
            : Boolean(item?.is_printing);

        let lastActivityAt;
        if (isPrinting) {
            lastActivityAt = null;
        } else if (activityMap instanceof Map && activityMap.has(id)) {
            lastActivityAt = activityMap.get(id)?.lastActivityAt ?? null;
        } else if (Object.prototype.hasOwnProperty.call(item || {}, 'last_activity_at')) {
            lastActivityAt = item.last_activity_at;
        } else {
            lastActivityAt = undefined;
        }

        if (lastActivityAt === undefined && !isPrinting) {
            return {
                ...item,
                is_printing: false,
                is_idle: false,
                idle_label: null,
                idle_ms: null,
            };
        }

        return {
            ...item,
            ...runtimeStatusFromActivity({
                isPrinting,
                lastActivityAt: isPrinting ? null : lastActivityAt,
                now,
            }),
        };
    });
}

/** @deprecated Prefer applyPrinterRuntimeFlags — mantido para compat. */
export function applyIdleFlags(items, idleMap) {
    return (items || []).map((item) => {
        const id = item?.printer_uuid || item?.id;
        const idle = id && idleMap instanceof Map ? idleMap.get(id) : null;
        if (!idle || item?.is_printing) {
            return {
                ...item,
                is_idle: false,
                idle_label: null,
                idle_ms: null,
                last_activity_at: item?.is_printing ? null : (item?.last_activity_at ?? null),
            };
        }
        return {
            ...item,
            is_idle: true,
            idle_label: idle.idleLabel,
            idle_ms: idle.idleMs,
            last_activity_at: idle.lastActivityAt ?? null,
        };
    });
}

/** Busca o job mais recente de cada impressora (limit=1). */
export async function fetchLastJobsForPrinters(apiClient, printerIds) {
    const ids = [...new Set((printerIds || []).filter(Boolean))];
    if (ids.length === 0) {
        return [];
    }
    const batches = await Promise.all(
        ids.map((id) => (
            apiClient.get(`jobs?printer_uuid=${encodeURIComponent(id)}&limit=1`)
                .then((res) => res.data.data?.jobs || [])
                .catch(() => [])
        )),
    );
    return batches.flat();
}

let openRestrictionPrinterIds = new Set();

export function rememberOpenRestrictions(restrictions) {
    openRestrictionPrinterIds = new Set(
        (restrictions || [])
            .filter((row) => row?.open && row.printer_uuid)
            .map((row) => row.printer_uuid),
    );
}

/**
 * Monta mapa de atividade/ociosidade com o último job real de cada impressora parada.
 */
export async function loadIdlePrinterMap(apiClient, printers, inProgressJobs = null, restrictions) {
    const active = filterActivePrinters(printers);
    let restrictionRows = restrictions;
    if (!Array.isArray(restrictionRows)) {
        try {
            const response = await apiClient.get('printer-productivity-restrictions');
            restrictionRows = response.data?.restrictions || [];
        } catch {
            restrictionRows = [];
        }
    }
    rememberOpenRestrictions(restrictionRows);

    if (active.length === 0) {
        return {
            inProgressJobs: inProgressJobs || [],
            idleMap: new Map(),
            activityMap: new Map(),
            rows: [],
            restrictions: restrictionRows,
        };
    }

    let progressJobs = inProgressJobs;
    if (!Array.isArray(progressJobs)) {
        const inProgressRes = await apiClient.get('jobs?status=em_andamento&limit=200');
        progressJobs = inProgressRes.data.data?.jobs || [];
    }

    const busy = printersInProgressSet(progressJobs);
    const candidateIds = active
        .map((printer) => printer.id || printer.uuid)
        .filter((id) => id && !busy.has(id) && !openRestrictionForPrinter(restrictionRows, id));

    const lastJobs = await fetchLastJobsForPrinters(apiClient, candidateIds);
    const activityMap = buildPrinterActivityMap({
        printers: active,
        inProgressJobs: progressJobs,
        recentJobs: lastJobs,
        restrictions: restrictionRows,
    });
    const rows = buildIdlePrinterRows({
        printers: active,
        inProgressJobs: progressJobs,
        recentJobs: lastJobs,
        restrictions: restrictionRows,
    });

    return {
        inProgressJobs: progressJobs,
        idleMap: idleRowsToMap(rows),
        activityMap,
        rows,
        restrictions: restrictionRows,
    };
}

/** Atualiza mapa de atividade em notificação live (start/finish). */
export function patchActivityMapForNotification(activityMap, detail, {
    now = Date.now(),
    printerName,
} = {}) {
    const next = new Map(activityMap instanceof Map ? activityMap : []);
    const printerUuid = detail?.printerUuid;
    if (!printerUuid || openRestrictionPrinterIds.has(printerUuid)) {
        if (printerUuid) {
            next.delete(printerUuid);
        }
        return next;
    }

    if (detail.action === 'started') {
        next.delete(printerUuid);
        return next;
    }

    if (detail.action === 'finished') {
        const prev = next.get(printerUuid);
        next.set(printerUuid, {
            id: printerUuid,
            name: printerName || prev?.name || printerUuid,
            lastActivityAt: now,
        });
    }

    return next;
}

/** Filtra linhas ociosas a partir do mapa de atividade (tick no front). */
export function idleRowsFromActivityMap(activityMap, now = Date.now()) {
    const rows = [];
    (activityMap || new Map()).forEach((entry, id) => {
        const status = runtimeStatusFromActivity({
            isPrinting: false,
            lastActivityAt: entry?.lastActivityAt,
            now,
        });
        if (!status.is_idle) {
            return;
        }
        rows.push({
            id,
            name: entry?.name || id,
            lastActivityAt: entry?.lastActivityAt ?? null,
            idleMs: status.idle_ms,
            idleLabel: status.idle_label,
        });
    });
    rows.sort((a, b) => {
        if (a.idleMs == null && b.idleMs == null) {
            return String(a.name).localeCompare(String(b.name), 'pt-BR');
        }
        if (a.idleMs == null) return 1;
        if (b.idleMs == null) return -1;
        return b.idleMs - a.idleMs;
    });
    return rows;
}
